import { useProductSearchQuery } from '@api/product.api';
import { ProductSearchResponse } from '@api/types';
import throttle from 'lodash/throttle';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type MultilevelMenuContextProps = {
  children: React.ReactNode;
};

type MultilevelMenuContextType = {
  shouldHighlight: (categoryCode?: string) => boolean;
  handleCategoryEnter: (categoryCode?: string) => void;
  hasNestedCategories: (categoryCode?: string) => number | undefined;
  productData?: ProductSearchResponse['data'];
  openCategory: string | undefined | null;
  leftStyle?: number;
  megaMenuBlockRef?: React.RefObject<HTMLDivElement>;
};

export const MultilevelMenuContext = createContext<MultilevelMenuContextType | null>(null);

export const MultilevelMenuProvider = ({ children }: MultilevelMenuContextProps) => {
  const [openCategory, setOpenCategory] = useState<string | undefined | null>(null);
  const [leftStyle, setLeftStyle] = useState<number>();
  const megaMenuBlockRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (megaMenuBlockRef.current) {
        const megamenuBlockRect = megaMenuBlockRef.current.getBoundingClientRect();
        setLeftStyle(megamenuBlockRect.left);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const shouldHighlight = useCallback(
    (categoryCode?: string) => {
      return openCategory === categoryCode;
    },
    [openCategory]
  );

  const { currentData } = useProductSearchQuery({});

  const categoryEnterFunction = (categoryCode?: string) => {
    if (openCategory === categoryCode) {
      setOpenCategory(null);
    } else {
      setOpenCategory(categoryCode);
    }
  };

  const throttledCategoryEnter = throttle(categoryEnterFunction, 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCategoryEnter = useCallback(throttledCategoryEnter, []);

  useEffect(() => {
    return () => throttledCategoryEnter.cancel();
  }, [throttledCategoryEnter]);

  const hasNestedCategories = useCallback(
    (categoryCode?: string) => {
      return currentData?.data?.categories?.find((category) => category.code === categoryCode)
        ?.nestedCategories?.length;
    },
    [currentData?.data?.categories]
  );

  const contextValue = useMemo(
    () => ({
      shouldHighlight,
      handleCategoryEnter,
      hasNestedCategories,
      productData: currentData?.data,
      openCategory,
      leftStyle,
      megaMenuBlockRef,
    }),
    [
      shouldHighlight,
      handleCategoryEnter,
      hasNestedCategories,
      currentData?.data,
      openCategory,
      leftStyle,
      megaMenuBlockRef,
    ]
  );
  return (
    <MultilevelMenuContext.Provider value={contextValue}>{children}</MultilevelMenuContext.Provider>
  );
};

export const useMultilevelMenuContext = () => {
  const context = useContext(MultilevelMenuContext);
  if (!context) {
    throw new Error('useMultilevelMenuContext must be used within a MultilevelMenuProvider');
  }
  return context;
};
